import type { RolePermissions } from "../types";
import type { ConfigurationContextValue } from "./ConfigurationContext";

export const defaultDarkModeLogo = "./logos/logo_atomic_crm_dark.svg";
export const defaultLightModeLogo = "./logos/logo_atomic_crm_light.svg";

export const defaultCurrency = "USD";

export const defaultTitle = "Atomic CRM";

export const defaultCompanySectors = [
  { value: "communication-services", label: "Communication Services" },
  { value: "consumer-discretionary", label: "Consumer Discretionary" },
  { value: "consumer-staples", label: "Consumer Staples" },
  { value: "energy", label: "Energy" },
  { value: "financials", label: "Financials" },
  { value: "health-care", label: "Health Care" },
  { value: "industrials", label: "Industrials" },
  { value: "information-technology", label: "Information Technology" },
  { value: "materials", label: "Materials" },
  { value: "real-estate", label: "Real Estate" },
  { value: "utilities", label: "Utilities" },
];

export const defaultDealStages = [
  { value: "opportunity", label: "Opportunity" },
  { value: "proposal-sent", label: "Proposal Sent" },
  { value: "in-negociation", label: "In Negotiation" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "delayed", label: "Delayed" },
];

export const defaultDealPipelineStatuses = ["won"];

export const defaultDealCategories = [
  { value: "other", label: "Other" },
  { value: "copywriting", label: "Copywriting" },
  { value: "print-project", label: "Print project" },
  { value: "ui-design", label: "UI Design" },
  { value: "website-design", label: "Website design" },
];

export const defaultNoteStatuses = [
  { value: "cold", label: "Cold", color: "#7dbde8" },
  { value: "warm", label: "Warm", color: "#e8cb7d" },
  { value: "hot", label: "Hot", color: "#e88b7d" },
  { value: "in-contract", label: "In Contract", color: "#a4e87d" },
];

// 20 B2B trade task types — PRD §10
export const defaultTaskTypes = [
  // 阶段一：询盘 / 客户开发
  { value: "send-dev-email",      label: "发送开发信" },
  { value: "send-catalog",        label: "发送产品目录" },
  { value: "follow-inquiry",      label: "跟进询盘" },
  { value: "confirm-requirements",label: "确认需求" },
  // 阶段二：报价
  { value: "send-quotation",      label: "发送报价单" },
  { value: "follow-quotation",    label: "跟进报价反馈" },
  { value: "negotiate-price",     label: "价格谈判" },
  { value: "send-sample",         label: "寄送样品" },
  // 阶段三：样品确认
  { value: "follow-sample-receipt",  label: "跟进样品收到" },
  { value: "follow-sample-approval", label: "跟进样品确认" },
  // 阶段四：订单执行
  { value: "send-pi",             label: "发送 PI" },
  { value: "follow-deposit",      label: "跟进定金付款" },
  { value: "follow-production",   label: "跟进生产进度" },
  { value: "arrange-inspection",  label: "安排验货" },
  { value: "follow-balance",      label: "跟进尾款付款" },
  { value: "follow-shipment",     label: "跟进发货" },
  // 阶段五：售后 / 客户维护
  { value: "confirm-receipt",     label: "确认货物签收" },
  { value: "handle-complaint",    label: "处理投诉/索赔" },
  { value: "seasonal-greeting",   label: "节日/季节问候" },
  { value: "repurchase-followup", label: "复购/新品推介" },
];

export const defaultVipThresholds = {
  silver: 1000,
  gold: 5000,
  platinum: 15000,
  diamond: 50000,
};

export const defaultVipAccountingCurrency = "USD";

export const defaultRolePermissions: RolePermissions = {
  contacts:            { admin: "all", manager: "all", sales: "own", merchandiser: "read", finance: "read" },
  companies:           { admin: "all", manager: "all", sales: "own", merchandiser: "read", finance: "read" },
  deals:               { admin: "all", manager: "all", sales: "own", merchandiser: "none", finance: "none" },
  inquiries:           { admin: "all", manager: "all", sales: "own", merchandiser: "read", finance: "none" },
  quotations:          { admin: "all", manager: "all", sales: "own", merchandiser: "read", finance: "read" },
  orders:              { admin: "all", manager: "all", sales: "own", merchandiser: "read", finance: "read" },
  members:             { admin: "all", manager: "all", sales: "read", merchandiser: "none", finance: "all" },
  logistics:           { admin: "all", manager: "all", sales: "read", merchandiser: "all", finance: "none" },
  products:            { admin: "all", manager: "all", sales: "read", merchandiser: "read", finance: "none" },
  suppliers:           { admin: "all", manager: "read", sales: "none", merchandiser: "all", finance: "read" },
  purchase_orders:     { admin: "all", manager: "read", sales: "none", merchandiser: "all", finance: "read" },
  quality_inspections: { admin: "all", manager: "read", sales: "none", merchandiser: "all", finance: "none" },
  returns:             { admin: "all", manager: "all", sales: "read", merchandiser: "all", finance: "read" },
  ar:                  { admin: "all", manager: "all", sales: "read", merchandiser: "none", finance: "all" },
  expenses:            { admin: "all", manager: "all", sales: "own", merchandiser: "own", finance: "all" },
  complaints:          { admin: "all", manager: "all", sales: "read", merchandiser: "read", finance: "all" },
  reports:             { admin: "all", manager: "all", sales: "read", merchandiser: "none", finance: "all" },
  workflow:            { admin: "all", manager: "read", sales: "none", merchandiser: "none", finance: "none" },
  audit_log:           { admin: "all", manager: "read", sales: "none", merchandiser: "none", finance: "none" },
  settings:            { admin: "all", manager: "none", sales: "none", merchandiser: "none", finance: "none" },
  factory_cost:        { admin: "all", manager: "all", sales: "none", merchandiser: "none", finance: "all" },
};

export const defaultConfiguration: ConfigurationContextValue = {
  companySectors: defaultCompanySectors,
  currency: defaultCurrency,
  dealCategories: defaultDealCategories,
  dealPipelineStatuses: defaultDealPipelineStatuses,
  dealStages: defaultDealStages,
  noteStatuses: defaultNoteStatuses,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
  vipAccountingCurrency: defaultVipAccountingCurrency,
  vipThresholds: defaultVipThresholds,
  rolePermissions: defaultRolePermissions,
};
